import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, errorOf, ok } from "./api";
import { messageFor } from "./error-copy";

type Schemas = components["schemas"];
export type Product = Schemas["Product"];
export type ProductItem = Product["items"][number];
export type ProductType = Product["type"];
export type Unit = ProductItem["unit"];
export type Fulfillment = ProductItem["fulfillment"];
export type NewItem = Schemas["CreateProductItemBody"];

export const PRODUCT_TYPE: Record<ProductType, string> = {
  water: "Water",
  goods: "Goods",
  service: "Service",
};

export const UNIT: Record<Unit, string> = {
  litre: "Litres",
  piece: "Pieces",
  kg: "Kilograms",
  hour: "Hours",
  visit: "Visits",
};

/** What happens when it's ordered (SCHEMA.md → Catalogue). */
export const FULFILLMENT: Record<Fulfillment, { label: string; help: string }> = {
  truck_load: { label: "Truck load", help: "A truck fills at a fill site and delivers" },
  parcel: { label: "Carried", help: "Carried to the customer, no fill site" },
  on_site: { label: "Visit", help: "Done at the customer's place, e.g. tank cleaning" },
  none: { label: "Nothing to deliver", help: "No delivery" },
};

export const productQuery = (id: string) =>
  queryOptions({
    queryKey: ["catalog", "product", id],
    queryFn: () => ok(api.GET("/v1/admin/catalog/products/{id}", { params: { path: { id } } })),
  });

export const productActions = {
  create: (body: Schemas["CreateProductBody"]) => ok(api.POST("/v1/admin/catalog/products", { body })),
  update: (id: string, body: Schemas["UpdateProductBody"]) =>
    ok(api.PATCH("/v1/admin/catalog/products/{id}", { params: { path: { id } }, body })),
  addItem: (id: string, body: NewItem) =>
    ok(api.POST("/v1/admin/catalog/products/{id}/items", { params: { path: { id } }, body })),
  updateItem: (id: string, itemId: string, body: Schemas["UpdateProductItemBody"]) =>
    ok(
      api.PATCH("/v1/admin/catalog/products/{id}/items/{item_id}", {
        params: { path: { id, item_id: itemId } },
        body,
      }),
    ),
};

/** On a product form, the only unique value is the SKU: say so. */
export const productError = (error: unknown) => {
  const e = errorOf(error);
  return e.code === "DUPLICATE_ENTRY"
    ? "Another item already has this SKU. SKUs are unique in the company."
    : messageFor(e);
};
